import { apiFetch, apiFetchBlob, apiSendForBlob, apiSendJson } from './client';
import type {
  DirectoryUser,
  QrBackground,
  QrForeground,
  QrImageFormat,
  QrLogoStyle,
  VCardContact,
  VCardPreview,
  VCardProfile,
} from './types';

/** The caller's own contact details. Available to every signed-in user, group or no group. */
export function getMyVCardProfile(): Promise<VCardProfile> {
  return apiFetch<VCardProfile>('/api/vcard/profile');
}

/**
 * A colleague's contact details. The API restricts this to members of a group, and answers `200` with a
 * `warning` - rather than an error - when the directory can only supply part of it.
 */
export function getVCardProfileFor(userId: string): Promise<VCardProfile> {
  return apiFetch<VCardProfile>(`/api/vcard/profile/${encodeURIComponent(userId)}`);
}

/**
 * Finds the colleague a card is being built for. A different endpoint from the member-management search in
 * `directory.ts`: same results, but open to any group member rather than to group admins only.
 */
export function searchVCardDirectory(
  search: string,
  take = 10,
  signal?: AbortSignal,
): Promise<DirectoryUser[]> {
  const query = new URLSearchParams({ search, take: String(take) });
  return apiFetch<DirectoryUser[]>(`/api/vcard/directory?${query.toString()}`, { signal });
}

export function previewVCard(contact: VCardContact): Promise<VCardPreview> {
  return apiSendJson<VCardPreview>('/api/vcard/preview', 'POST', contact);
}

/**
 * Renders the card as a QR code. A POST because the payload *is* the contact - there is no stored code to
 * fetch an image for.
 */
export function renderVCardImage(
  contact: VCardContact,
  format: QrImageFormat = 'png',
  size = 10,
  download = false,
  foreground?: QrForeground,
  background?: QrBackground,
  logoStyle?: QrLogoStyle,
): Promise<Blob> {
  const query = new URLSearchParams({ format, size: String(size), download: String(download) });
  if (foreground) {
    query.set('foreground', foreground);
  }

  if (background) {
    query.set('background', background);
  }

  if (logoStyle) {
    query.set('logoStyle', logoStyle);
  }

  return apiSendForBlob(`/api/vcard/image?${query.toString()}`, 'POST', contact);
}

/** The Entra photo a card would carry, before any code stores it. Without an id, the caller's own. */
export function getEntraPhoto(userId?: string | null): Promise<Blob> {
  const query = userId ? `?userId=${encodeURIComponent(userId)}` : '';
  return apiFetchBlob(`/api/vcard/photo${query}`);
}
